import {
  getRedirectResult,
  onAuthStateChanged,
  onIdTokenChanged,
  signOut,
} from "firebase/auth";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { API_CLIENT, API_CONFIG, isLocalApiServer } from "../../../api/config";
import {
  deleteStaticFrontSession,
  syncStaticFrontSession,
} from "../../../api/staticFront";
import { User, userFromFirestore } from "../../../domain/User";
import { auth } from "../../../firebase";
import { devFastCache } from "../../../utils/devFastCache";
import { AppLoginWaiting } from "../../AppLoginWaiting";

import { LoginView } from "./LoginView";
import { ResendEmailVerification } from "./ResendEmailVerification";

declare global {
  interface Window {
    signOut: () => unknown;
  }
}

type AppAuthState = {
  isLoggedIn: boolean;
  user: null | User;
};

const appAuthCtx = createContext<AppAuthState>({
  isLoggedIn: false,
  user: null,
});

let isLastAuthorized = false;

onIdTokenChanged(auth, (user) => {
  if (!user) {
    void deleteStaticFrontSession();
    return;
  }
  void user.getIdToken().then(syncStaticFrontSession);
});

onAuthStateChanged(auth, (user) => {
  if (!user && isLastAuthorized) {
    location.href = API_CONFIG.URL_LOGOUT;
    return;
  }
  isLastAuthorized = !!user;
});

export const useAppAuth = () => {
  return useContext(appAuthCtx);
};

/**
 * @description 認証情報のprovider
 * ログイン画面を描画。ログイン後は何も表示しない
 * firebase Authentificationを経由し、Auth0でログインをするカスタム認証を利用
 * Auth0経由でログインをすると、Firebase Authenticationにサーバがユーザー登録する
 */
export const AppAuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // 認証初期化
  const [appAuthInitialized, setAppAuthInitialized] = useState(false);
  // ログイン情報
  const [appUser, setAppUser] = useState<AppAuthState>({
    isLoggedIn: false,
    user: null,
  });
  // ログアウト関数
  // @ts-ignore
  window.signOut = async () => {
    // ログアウト前にログイン情報を初期化
    setAppUser({
      isLoggedIn: false,
      user: null,
    });
    // firebaseのサインアウト関数を実行
    // /o/ 配下の配信に使うcookieの削除は onIdTokenChanged の !user 側で行う
    await signOut(auth);
    // Auth0のログアウトURLに遷移
    location.href = API_CONFIG.URL_LOGOUT;
  };

  useEffect(() => {
    (async () => {
      await getRedirectResult(auth);
      const authUser = auth.currentUser;

      if (authUser) {
        const getUserTokenPromise = authUser.getIdToken(true).then((token) => {
          getUserToken = () => authUser.getIdToken();
          return token;
        });
        let getUserToken = async () => {
          return getUserTokenPromise;
        };
        let requestHookInjected = false;
        const initAuth = (authRes: any) => {
          try {
            // firebaseから取得したユーザーデータを状態にセット
            setAppUser({
              isLoggedIn: true,
              user: userFromFirestore({
                ...authRes.data,
                id: authRes.data.uid,
                is_disabled: false,
              }),
            });

            // トークン＆APIキー取得をAPI通信時に共通で埋め込む
            if (!requestHookInjected) {
              API_CLIENT.interceptors.request.use(
                async (config) => {
                  const idToken = await getUserToken();
                  config.headers["X-API-KEY"] = API_CONFIG.API_KEY;
                  config.headers.Authorization = `Bearer ${idToken}`;
                  if (isLocalApiServer) {
                    config.headers["X-Forwarded-Authorization"] =
                      `Bearer ${idToken}`;
                  }
                  return config;
                },
                (error) => {
                  alert(error);
                  return Promise.reject(error);
                }
              );
              requestHookInjected = true;
            }
          } catch (e) {
            alert(e);
          } finally {
            setAppAuthInitialized(true);
          }
        };
        const authCache = devFastCache.get("authResData");
        // 別アカウントで再ログインした際にキャッシュ（前ユーザーの uid）と
        // 現在の Firebase 認証 uid が食い違うと、API リクエストの URL に古い uid、
        // Authorization ヘッダに新しいトークンが乗って 403 になる。
        // uid が一致するときだけキャッシュを利用する。
        if (authCache !== null && authCache.data?.uid === authUser.uid) {
          //getUserTokenPromise = Promise.resolve(authCache.idToken);
          initAuth({ data: authCache.data });
        }
        // 認証結果を元に、クライアントはIDトークンを取得し、APIサーバにアクセスする
        const idToken = await getUserToken();
        const authRes = await API_CLIENT.post("/signin", undefined, {
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": API_CONFIG.API_KEY,
            Authorization: `Bearer ${idToken}`,
            ...(isLocalApiServer && {
              "X-Forwarded-Authorization": `Bearer ${idToken}`,
            }),
          },
        });
        devFastCache.store("authResData", { idToken, data: authRes.data });
        if (
          authCache === null ||
          JSON.stringify({
            ...authCache.data,
            latest_logined_at: "",
            updated_at: "",
          }) !==
            JSON.stringify({
              ...authRes.data,
              latest_logined_at: "",
              updated_at: "",
            })
        ) {
          initAuth(authRes);
        }
      }
      setAppAuthInitialized(true);
    })();
  }, []);
  if (!appAuthInitialized)
    // 初期化できていなければ待機画面を表示
    return <AppLoginWaiting />;
  // ログインできていれば画面を描画する
  if (appUser.isLoggedIn) {
    return (
      <appAuthCtx.Provider value={appUser}>{children}</appAuthCtx.Provider>
    );
  }

  // URLを取得し、条件を満たしていればメール検証のページに遷移する
  const urlParams = new URLSearchParams(window.location.search);
  const auth0Uid = urlParams.get("u");
  const state = urlParams.get("state");
  const hash = window.location.hash;
  const isEmailVerificationSettings = hash === "#/email_verification_settings";

  // 最後にアクセスしたページの履歴をチェックする
  // auth0にリダイレクトした時にログイン画面に遷移
  return (
    <>
      {auth0Uid && state && isEmailVerificationSettings ? (
        <ResendEmailVerification idp_uid={auth0Uid} />
      ) : (
        <LoginView />
      )}
    </>
  );
};
